import { getConfig } from '../config';
import { tGlobal } from '../services/translation';
import countries from './countries.json';

export type Country = {
  name: string;
  code: string;
};

const countriesList: Country[] = [];

export function getCountries(): Country[] {
  if (countriesList.length > 0) {
    return countriesList;
  }

  const topCountries = [
    { code: 'us', name: countries['us'] },
    { code: 'gb', name: countries['gb'] },
    { code: 'fr', name: countries['fr'] },
    { code: 'au', name: countries['au'] },
    { code: 'ca', name: countries['ca'] },
    { code: 'de', name: countries['de'] },
    { code: 'separator', name: '' },
  ];

  // cached countries
  const cachedCountries: { [key: string]: boolean } = {};
  // top countries
  for (let i = 0; i < topCountries.length; i += 1) {
    const country = topCountries[i];
    if (cachedCountries[country.code]) {
      continue;
    }

    cachedCountries[country.code] = true;
    countriesList.push(country);
  }

  // other countries, skipping those already cached
  for (const [code, name] of Object.entries(countries)) {
    if (cachedCountries[code]) {
      continue;
    }

    cachedCountries[code] = true;
    countriesList.push({ code, name });
  }

  return countriesList;
}

export function getCountryName(code: string) {
  if (countriesList.length > 0) {
    return countriesList.find((country) => country.code === code)?.name;
  }

  return countries[code as keyof typeof countries];
}

export function getLanguageSupportedOnBlym() {
  const { supportedKeywordLanguages } = getConfig();
  return [
    { value: 'en', label: tGlobal('english') },
    { value: 'fr', label: tGlobal('french') },
  ].filter((language) => supportedKeywordLanguages.includes(language.value));
}

export function getLanguages() {
  const { supportedKeywordLanguages } = getConfig();
  return [
    { value: 'en', label: tGlobal('english') },
    { value: 'fr', label: tGlobal('french') },
    { value: 'nl', label: tGlobal('dutch') },
    { value: 'it', label: tGlobal('italian') },
    { value: 'es', label: tGlobal('spanish') },
    { value: 'de', label: tGlobal('german') },
    { value: 'sv', label: tGlobal('swedish') },
    { value: 'no', label: tGlobal('norwegianBokmal') },
    { value: 'pt-PT', label: tGlobal('portuguesePt') },
    { value: 'pt-BR', label: tGlobal('portugueseBr') },
    { value: 'da', label: tGlobal('danish') },
    { value: 'fi', label: tGlobal('finnish') },
    { value: 'pl', label: tGlobal('polish') },
    { value: 'ro', label: tGlobal('romanian') },
  ].filter((language) => supportedKeywordLanguages.includes(language.value));
}
export function convertCountryToLanguageCode(country: string) {
  const countryCode = country.toLowerCase();

  // Map country codes to language codes based on major countries
  switch (countryCode) {
    case 'fr':
      return 'fr';
    case 'nl':
      return 'nl';
    case 'it':
      return 'it';
    case 'es':
      return 'es';
    case 'de':
    case 'at':
    case 'ch':
      return 'de';
    case 'se':
      return 'sv';
    case 'no':
      return 'no';
    case 'pt':
      return 'pt-PT';
    case 'br':
      return 'pt-BR';
    case 'dk':
      return 'da';
    case 'fi':
      return 'fi';
    case 'pl':
      return 'pl';
    case 'ro':
      return 'ro';
    default:
      return 'en';
  }
}

export const countriesOptions = getCountries().map(function (country: Country) {
  if (country.code === 'separator') {
    return {
      value: country.code,
      label: '',
      separator: true,
    };
  }
  // TODO: translate the country name here, probably need a extra translation file, only for countries
  return {
    value: country.code,
    label: country.name,
  };
});

/**
 * Falls back to English if language for `countryCode` is not found in the list
 */
export function getLanguage(countryCode: string) {
  const languages = getLanguages();
  const index = languages.findIndex((lang) => lang.value === countryCode);
  if (index === -1) {
    return tGlobal('english');
  }
  return languages[index].label;
}
